import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import CloseIcon from '@material-ui/icons/Close'
import AddIcon from '@material-ui/icons/Add'
import { DateTime } from 'luxon'
import NumberFormat from 'react-number-format'
import { useQuery } from '@tanstack/react-query'

import { Container } from '../../../../components'
import { SelectorPopover } from '../../../../components/TemplateScheduleGantt/Components/SelectorPopover'
import { axiosInstance } from '../../../../helpers'
import {
    useCompany,
    useFinderSelection,
    useRootInfrastructure,
} from '../../../../hooks'
import { useEzSchedule } from '../../../../hooks/useEzSchedule/useEzSchedule'
import { usePopover } from '../../../../hooks/usePopover'
import { useTimezone } from '../../../../contexts/timezone'
import {
    ListVendor,
    Schedule,
    Service,
    TemplateUnit,
    Unit,
} from '../../../../models'
import { UpdateScheduleRequest } from '../../../../store'
import { UnitSelector } from '../../../Scheduler_v2/Modals/ScheduleModal/components/UnitSelector'
import { ServiceRow } from '../../../Scheduler_v2/Modals/ScheduleModal/components/ServiceRow'
import { DraftWizardStep, TermLeaseContext } from './types'

interface Props {
    open: boolean
    schedule: Schedule
    serviceList: Service[]
    onClose: () => void
    updateSchedule: (req: UpdateScheduleRequest) => Promise<unknown>
    onConfirmed: (result: {
        openedScheduler: boolean
        scheduleId: number
    }) => void
}

const fromPickerDate = (value: DateTime | null): DateTime | null => {
    if (!value || !value.isValid) return null
    return value.startOf('day')
}

/** Inclusive calendar-day length of a project window. */
function projectWindowDays(
    startIso: string | null | undefined,
    endIso: string | null | undefined,
): number | null {
    if (!startIso || !endIso) return null
    const start = DateTime.fromISO(startIso).startOf('day')
    const end = DateTime.fromISO(endIso).startOf('day')
    if (!start.isValid || !end.isValid || end < start) return null
    return Math.round(end.diff(start, 'days').days) + 1
}

const SHORT_VS_SOURCE_DAYS = 3

export const DraftConfirmWizard = (props: Props) => {
    const {
        open,
        schedule,
        serviceList,
        onClose,
        updateSchedule,
        onConfirmed,
    } = props
    const theme = useTheme()
    const { currentTimezone } = useTimezone()

    const includeScheduleSteps = schedule.schedule_review_status === 'PENDING'
    const includeProjectStep = schedule.review_status === 'DRAFT'

    const [stepIndex, setStepIndex] = useState(0)
    const [submitting, setSubmitting] = useState(false)
    const [stepError, setStepError] = useState<string | null>(null)

    // Project step state
    const [name, setName] = useState(schedule.name)
    const [startDate, setStartDate] = useState(
        DateTime.fromISO(schedule.start_date).setZone(currentTimezone),
    )
    const [endDate, setEndDate] = useState(
        DateTime.fromISO(schedule.end_date).setZone(currentTimezone),
    )
    const [wantBudget, setWantBudget] = useState<'yes' | 'no' | null>(
        schedule.baseline_budget > 0 ? 'yes' : null,
    )
    const [budgetAmount, setBudgetAmount] = useState(
        schedule.baseline_budget > 0 ? String(schedule.baseline_budget) : '',
    )

    const [phaseId, setPhaseId] = useState<number | undefined>(undefined)
    const unitsFlushRef = useRef<(() => void | Promise<void>) | null>(null)
    const unitsSelectedCountRef = useRef(0)
    const scheduleOnlyRepinDoneRef = useRef(false)

    const { tree } = useRootInfrastructure(includeScheduleSteps && open)
    const { vendorList, getVendorList } = useCompany({ cleanUp: true })

    useEffect(() => {
        if (!open || !includeScheduleSteps) return
        const apartmentId =
            typeof schedule.apartment === 'object'
                ? schedule.apartment?.id
                : schedule.apartment
        if (apartmentId) {
            getVendorList({ params: { apartment: apartmentId } })
        }
    }, [open, includeScheduleSteps, schedule.apartment])

    const {
        ezSchedule,
        ezScheduleLoading,
        ezScheduleError,
        refetchEzSchedule,
        updateEzSchedule,
        setTemplateUnits,
        addTemplateService,
        deleteTemplateService,
        addTemplateServiceVendor,
        deleteTemplateServiceVendor,
        updateTemplateServiceVendor,
        updateTemplateService,
    } = useEzSchedule({
        scheduleId: schedule.id,
        phaseId,
        enabled: open && includeScheduleSteps,
    })

    const scheduleLoadFailed =
        includeScheduleSteps &&
        !ezScheduleLoading &&
        !ezSchedule &&
        Boolean(ezScheduleError)
    // CONFIRMED shell + PENDING review with no ez-schedule: no project step to
    // fall back on — Close / Retry only (do not mark COMPLETE).
    const scheduleOnlyLoadFailed = scheduleLoadFailed && !includeProjectStep

    const leaseContextQuery = useQuery({
        queryKey: ['term-lease-context', schedule.id],
        queryFn: async () => {
            const res = await axiosInstance.get<TermLeaseContext>(
                `/workorder/schedule/${schedule.id}/term-lease-context/`,
            )
            return res.data
        },
        enabled: open && includeProjectStep,
        staleTime: 5 * 60 * 1000,
    })

    const steps: DraftWizardStep[] = useMemo(() => {
        const built: DraftWizardStep[] = []
        if (includeProjectStep) {
            built.push({ kind: 'project' })
        }
        if (includeScheduleSteps && ezSchedule) {
            const phases = ezSchedule.schedule_phases ?? []
            const phaseCount = Math.max(phases.length, 1)
            const ordered =
                phases.length > 0
                    ? [...phases].sort((a, b) => a.phase_index - b.phase_index)
                    : []
            if (ordered.length === 0) {
                // Fallback: still walk schedule concerns once using first phase data
                const fallbackPhaseId = ezSchedule.phases?.[0]?.phase?.id
                if (fallbackPhaseId != null) {
                    built.push(
                        {
                            kind: 'units',
                            phaseId: fallbackPhaseId,
                            phaseIndex: 1,
                            phaseCount: 1,
                        },
                        {
                            kind: 'dates',
                            phaseId: fallbackPhaseId,
                            phaseIndex: 1,
                            phaseCount: 1,
                        },
                        {
                            kind: 'services',
                            phaseId: fallbackPhaseId,
                            phaseIndex: 1,
                            phaseCount: 1,
                        },
                    )
                }
            } else {
                ordered.forEach((sp) => {
                    built.push(
                        {
                            kind: 'units',
                            phaseId: sp.id,
                            phaseIndex: sp.phase_index,
                            phaseCount,
                        },
                        {
                            kind: 'dates',
                            phaseId: sp.id,
                            phaseIndex: sp.phase_index,
                            phaseCount,
                        },
                        {
                            kind: 'services',
                            phaseId: sp.id,
                            phaseIndex: sp.phase_index,
                            phaseCount,
                        },
                    )
                })
            }
        }
        return built
    }, [includeProjectStep, includeScheduleSteps, ezSchedule])

    const currentStep = steps[stepIndex]
    // Wait for ez-schedule while loading; on failure unlock so the PM can still
    // confirm the project shell (schedule review stays PENDING for a retry).
    const scheduleStepsReady =
        !includeScheduleSteps || !!ezSchedule || scheduleLoadFailed
    const isLastStep =
        steps.length > 0 && stepIndex >= steps.length - 1 && scheduleStepsReady

    useEffect(() => {
        if (!open) {
            scheduleOnlyRepinDoneRef.current = false
            return
        }
        setStepIndex(0)
        setStepError(null)
        setName(schedule.name)
        setStartDate(
            DateTime.fromISO(schedule.start_date).setZone(currentTimezone),
        )
        setEndDate(DateTime.fromISO(schedule.end_date).setZone(currentTimezone))
        setWantBudget(schedule.baseline_budget > 0 ? 'yes' : null)
        setBudgetAmount(
            schedule.baseline_budget > 0
                ? String(schedule.baseline_budget)
                : '',
        )
    }, [open, schedule.id])

    // Schedule-only path (shell already confirmed): re-pin once on open so
    // work dates match the current project window before the Dates step.
    useEffect(() => {
        if (!open) return
        if (includeProjectStep || !includeScheduleSteps) return
        if (!schedule.bulk_schedule_source?.id) return
        if (scheduleOnlyRepinDoneRef.current) return
        scheduleOnlyRepinDoneRef.current = true
        let cancelled = false
        ;(async () => {
            try {
                await axiosInstance.post(
                    `/workorder/schedule/${schedule.id}/repin-bulk-schedule-dates/`,
                )
                if (!cancelled) {
                    await refetchEzSchedule()
                }
            } catch {
                // Dates step remains editable if re-pin fails.
            }
        })()
        return () => {
            cancelled = true
        }
    }, [
        open,
        includeProjectStep,
        includeScheduleSteps,
        schedule.id,
        schedule.bulk_schedule_source?.id,
    ])

    useEffect(() => {
        if (currentStep?.phaseId != null) {
            setPhaseId(currentStep.phaseId)
        }
    }, [currentStep?.phaseId])

    const selectedPhase = useMemo(() => {
        if (!ezSchedule || phaseId == null) return null
        return ezSchedule.phases.find((p) => p.phase?.id === phaseId) ?? null
    }, [ezSchedule, phaseId])

    const finishWizard = async () => {
        setSubmitting(true)
        setStepError(null)
        try {
            const body: UpdateScheduleRequest['body'] = {}
            if (includeProjectStep) {
                body.review_status = 'CONFIRMED'
            }
            // Only clear PENDING when the PM actually walked the schedule steps.
            if (includeScheduleSteps && ezSchedule && !scheduleLoadFailed) {
                body.schedule_review_status = 'COMPLETE'
            }
            // Ensure confirmed when completing a schedule-only oddball path
            if (!includeProjectStep && schedule.review_status === 'DRAFT') {
                body.review_status = 'CONFIRMED'
            }
            if (Object.keys(body).length > 0) {
                await updateSchedule({
                    scheduleId: schedule.id,
                    body,
                })
            }
            onConfirmed({
                openedScheduler:
                    includeScheduleSteps && !!ezSchedule && !scheduleLoadFailed,
                scheduleId: schedule.id,
            })
        } catch (e) {
            setStepError('Could not confirm this project. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    const handleContinue = async () => {
        if (!currentStep) return
        setStepError(null)

        if (currentStep.kind === 'project') {
            if (!name.trim()) {
                setStepError('Please enter a project name.')
                return
            }
            if (!startDate?.isValid || !endDate?.isValid) {
                setStepError('Please set valid project start and end dates.')
                return
            }
            if (endDate <= startDate) {
                setStepError('Project end must be after project start.')
                return
            }
            if (wantBudget === null) {
                setStepError('Please choose whether to set a budget.')
                return
            }
            if (wantBudget === 'yes') {
                const amount = Number(budgetAmount)
                if (!Number.isFinite(amount) || amount < 0) {
                    setStepError('Enter a valid budget amount.')
                    return
                }
            }
            setSubmitting(true)
            try {
                await updateSchedule({
                    scheduleId: schedule.id,
                    body: {
                        name: name.trim(),
                        start_date: startDate.toISO()!,
                        end_date: endDate.toISO()!,
                        baseline_budget:
                            wantBudget === 'yes' ? Number(budgetAmount) : 0,
                    },
                })
                // Always re-pin when a bulk template was applied so work dates
                // match the (possibly edited) project window before Units/Dates.
                if (includeScheduleSteps && schedule.bulk_schedule_source?.id) {
                    await axiosInstance.post(
                        `/workorder/schedule/${schedule.id}/repin-bulk-schedule-dates/`,
                    )
                    await refetchEzSchedule()
                }
            } catch {
                setStepError(
                    includeScheduleSteps && schedule.bulk_schedule_source?.id
                        ? 'Could not save project details or update schedule dates. Please try again.'
                        : 'Could not save project details.',
                )
                setSubmitting(false)
                return
            }
            setSubmitting(false)
        }

        if (currentStep.kind === 'units') {
            setSubmitting(true)
            try {
                await unitsFlushRef.current?.()
            } catch {
                setStepError('Could not save unit selection. Please try again.')
                setSubmitting(false)
                return
            }
            setSubmitting(false)
            if (unitsSelectedCountRef.current < 1) {
                setStepError('Select at least one unit for this phase.')
                return
            }
        }

        if (currentStep.kind === 'dates') {
            const workStart = selectedPhase?.work_start_date
            const workEnd = selectedPhase?.work_end_date
            if (!workStart || !workEnd) {
                setStepError('Set work start and work end dates.')
                return
            }
            const ws = DateTime.fromISO(workStart).setZone(currentTimezone)
            const we = DateTime.fromISO(workEnd).setZone(currentTimezone)
            if (we <= ws) {
                setStepError('Work end must be after work start.')
                return
            }
            const ts = selectedPhase?.transfer_start_date
            const te = selectedPhase?.transfer_end_date
            if (ts && te) {
                const tStart = DateTime.fromISO(ts).setZone(currentTimezone)
                const tEnd = DateTime.fromISO(te).setZone(currentTimezone)
                if (tEnd <= tStart) {
                    setStepError('Transfer end must be after transfer start.')
                    return
                }
            }
        }

        if (currentStep.kind === 'services') {
            const services = selectedPhase?.template_services ?? []
            if (services.length < 1) {
                setStepError('Add at least one service for this phase.')
                return
            }
            const missingVendor = services.filter(
                (s) => s.template_service_vendors.length === 0,
            )
            if (missingVendor.length > 0) {
                setStepError(
                    `Assign a vendor to every service (${missingVendor
                        .slice(0, 3)
                        .map((s) => s.service.name)
                        .join(', ')}).`,
                )
                return
            }
        }

        if (isLastStep) {
            await finishWizard()
            return
        }
        setStepIndex((i) => i + 1)
    }

    const stepTitle = (() => {
        if (!currentStep) return 'Confirm Project'
        if (currentStep.kind === 'project') return 'Confirm project details'
        const phaseLabel =
            (currentStep.phaseCount ?? 1) > 1
                ? ` · Phase ${currentStep.phaseIndex}`
                : ''
        if (currentStep.kind === 'units') return `Confirm units${phaseLabel}`
        if (currentStep.kind === 'dates') return `Confirm dates${phaseLabel}`
        return `Confirm services & vendors${phaseLabel}`
    })()

    const continueLabel = scheduleOnlyLoadFailed
        ? 'Close'
        : isLastStep
        ? includeScheduleSteps && !!ezSchedule && !scheduleLoadFailed
            ? 'Confirm & open scheduler'
            : 'Confirm project'
        : 'Continue'

    const showScheduleLoading =
        includeScheduleSteps &&
        ezScheduleLoading &&
        !ezSchedule &&
        currentStep?.kind !== 'project'

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={
                currentStep?.kind === 'units' ||
                currentStep?.kind === 'services'
                    ? 'lg'
                    : 'md'
            }
            PaperProps={{
                style: {
                    minHeight: 560,
                    maxHeight: '90vh',
                    ...(currentStep?.kind === 'units'
                        ? { width: '960px', maxWidth: '96vw' }
                        : {}),
                },
            }}
        >
            <DialogTitle disableTypography>
                <Container
                    style={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Container style={{ flexDirection: 'column', flex: 1 }}>
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                            {stepTitle}
                        </Typography>
                        <Typography
                            variant="body2"
                            style={{ color: theme.palette.grey[700] }}
                        >
                            {schedule.name} · {schedule.schedule_id}
                            {steps.length > 0
                                ? ` · Step ${stepIndex + 1} of ${steps.length}`
                                : ''}
                        </Typography>
                    </Container>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        aria-label="Close"
                    >
                        <CloseIcon />
                    </IconButton>
                </Container>
            </DialogTitle>
            <DialogContent dividers style={{ minHeight: 360 }}>
                {scheduleLoadFailed && (
                    <Container
                        style={{
                            flexDirection: 'column',
                            gap: theme.spacing(1),
                            marginBottom: theme.spacing(2),
                            padding: theme.spacing(1.5),
                            backgroundColor: theme.palette.error.light,
                            borderRadius: 4,
                        }}
                    >
                        <Typography color="error" variant="body2">
                            {includeProjectStep
                                ? 'Couldn’t load schedule details for review. You can still confirm the project details below, then open this draft again to review units, dates, and services — or retry loading now.'
                                : 'Couldn’t load schedule details for review. Retry now, or close and open this project again later. Schedule review stays pending until you finish.'}
                        </Typography>
                        <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() => refetchEzSchedule()}
                            style={{ alignSelf: 'flex-start' }}
                        >
                            Retry loading schedule
                        </Button>
                    </Container>
                )}
                {scheduleOnlyLoadFailed && (
                    <Typography
                        variant="body1"
                        style={{ color: theme.palette.grey[800] }}
                    >
                        Units, dates, and services can’t be reviewed until
                        schedule details load successfully.
                    </Typography>
                )}
                {steps.length === 0 &&
                    !scheduleLoadFailed &&
                    includeScheduleSteps &&
                    ezScheduleLoading && (
                        <Container
                            style={{
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 280,
                            }}
                        >
                            <CircularProgress />
                        </Container>
                    )}
                {showScheduleLoading && (
                    <Container
                        style={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 280,
                        }}
                    >
                        <CircularProgress />
                    </Container>
                )}
                {!showScheduleLoading && currentStep?.kind === 'project' && (
                    <ProjectStep
                        name={name}
                        setName={setName}
                        scheduleId={schedule.schedule_id}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        wantBudget={wantBudget}
                        setWantBudget={setWantBudget}
                        budgetAmount={budgetAmount}
                        setBudgetAmount={setBudgetAmount}
                        leaseContext={leaseContextQuery.data}
                        leaseLoading={leaseContextQuery.isLoading}
                        timezone={currentTimezone}
                        bulkScheduleSource={
                            schedule.bulk_schedule_source ?? null
                        }
                    />
                )}
                {!showScheduleLoading &&
                    currentStep?.kind === 'units' &&
                    ezSchedule &&
                    phaseId != null &&
                    tree?.root && (
                        <UnitsStep
                            key={`units-${phaseId}`}
                            infrastructure={tree.root}
                            phaseTemplateUnits={
                                selectedPhase?.template_units ?? []
                            }
                            phaseId={phaseId}
                            setTemplateUnits={setTemplateUnits}
                            phaseIndex={currentStep.phaseIndex ?? 1}
                            phaseCount={currentStep.phaseCount ?? 1}
                            flushRef={unitsFlushRef}
                            selectedCountRef={unitsSelectedCountRef}
                        />
                    )}
                {!showScheduleLoading &&
                    currentStep?.kind === 'dates' &&
                    selectedPhase && (
                        <DatesStep
                            key={`dates-${phaseId}`}
                            workStartDate={selectedPhase.work_start_date}
                            workEndDate={selectedPhase.work_end_date}
                            transferStartDate={
                                selectedPhase.transfer_start_date
                            }
                            transferEndDate={selectedPhase.transfer_end_date}
                            scheduleStart={ezSchedule?.start_date}
                            scheduleEnd={ezSchedule?.end_date}
                            timezone={currentTimezone}
                            phaseIndex={currentStep.phaseIndex ?? 1}
                            phaseCount={currentStep.phaseCount ?? 1}
                            updateEzSchedule={updateEzSchedule}
                        />
                    )}
                {!showScheduleLoading &&
                    currentStep?.kind === 'services' &&
                    selectedPhase && (
                        <ServicesStep
                            key={`services-${phaseId}`}
                            templateServices={
                                selectedPhase.template_services ?? []
                            }
                            vendorList={vendorList as ListVendor[]}
                            serviceList={serviceList}
                            scheduleId={schedule.id}
                            workStartDate={selectedPhase.work_start_date}
                            workEndDate={selectedPhase.work_end_date}
                            phaseIndex={currentStep.phaseIndex ?? 1}
                            phaseCount={currentStep.phaseCount ?? 1}
                            timezone={currentTimezone}
                            addTemplateService={addTemplateService}
                            deleteTemplateService={deleteTemplateService}
                            updateTemplateService={updateTemplateService}
                            addTemplateServiceVendor={addTemplateServiceVendor}
                            deleteTemplateServiceVendor={
                                deleteTemplateServiceVendor
                            }
                            updateTemplateServiceVendor={
                                updateTemplateServiceVendor
                            }
                        />
                    )}
                {stepError && (
                    <Typography
                        color="error"
                        style={{ marginTop: theme.spacing(2) }}
                    >
                        {stepError}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions style={{ padding: theme.spacing(2) }}>
                <Button onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={scheduleOnlyLoadFailed ? onClose : handleContinue}
                    disabled={
                        submitting ||
                        (!scheduleOnlyLoadFailed &&
                            (steps.length === 0 ||
                                showScheduleLoading ||
                                // Only block while ez-schedule is still in flight —
                                // failures unlock Continue (see scheduleLoadFailed).
                                (includeScheduleSteps &&
                                    ezScheduleLoading &&
                                    !ezSchedule &&
                                    currentStep?.kind === 'project' &&
                                    stepIndex >= steps.length - 1)))
                    }
                >
                    {submitting ? (
                        <CircularProgress size={22} color="inherit" />
                    ) : (
                        continueLabel
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function ProjectStep(props: {
    name: string
    setName: (v: string) => void
    scheduleId: string
    startDate: DateTime
    setStartDate: (v: DateTime) => void
    endDate: DateTime
    setEndDate: (v: DateTime) => void
    wantBudget: 'yes' | 'no' | null
    setWantBudget: (v: 'yes' | 'no') => void
    budgetAmount: string
    setBudgetAmount: (v: string) => void
    leaseContext?: TermLeaseContext
    leaseLoading: boolean
    timezone: string
    bulkScheduleSource?: Schedule['bulk_schedule_source']
}) {
    const theme = useTheme()
    const moveOut = props.leaseContext?.move_out_date
        ? DateTime.fromISO(props.leaseContext.move_out_date).toFormat(
              'MMM d, yyyy',
          )
        : null
    const moveIn = props.leaseContext?.move_in_date
        ? DateTime.fromISO(props.leaseContext.move_in_date).toFormat(
              'MMM d, yyyy',
          )
        : null

    const projectDays = projectWindowDays(
        props.startDate.toISO(),
        props.endDate.toISO(),
    )
    const sourceDays = projectWindowDays(
        props.bulkScheduleSource?.start_date,
        props.bulkScheduleSource?.end_date,
    )
    const showShortWarning =
        projectDays != null &&
        sourceDays != null &&
        sourceDays - projectDays >= SHORT_VS_SOURCE_DAYS

    return (
        <Container style={{ flexDirection: 'column', gap: theme.spacing(2) }}>
            <Typography variant="body1">
                Review the project details below. They were prepared for your
                turn — adjust anything that looks off, then continue.
            </Typography>

            <TextField
                label="Project name"
                value={props.name}
                onChange={(e) => props.setName(e.target.value)}
                variant="outlined"
                fullWidth
                helperText="You can rename this if needed."
            />
            <TextField
                label="Project ID"
                value={props.scheduleId}
                variant="outlined"
                fullWidth
                disabled
                helperText="This ID cannot be changed."
            />

            <Container
                style={{
                    flexDirection: 'column',
                    gap: theme.spacing(0.5),
                    padding: theme.spacing(1.5),
                    borderRadius: 6,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    backgroundColor: theme.palette.grey[50],
                }}
            >
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    Lease timing for this turn
                </Typography>
                {props.leaseLoading ? (
                    <Typography variant="body2" color="textSecondary">
                        Loading lease timing…
                    </Typography>
                ) : (
                    <>
                        <Typography variant="body2">
                            <strong>Move-out:</strong>{' '}
                            {moveOut ?? 'Not available from lease data'}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Move-in:</strong>{' '}
                            {moveIn ?? 'Not available from lease data'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            If these don’t match what you know for your
                            property, update the project start and end dates
                            below.
                        </Typography>
                    </>
                )}
            </Container>

            {showShortWarning && (
                <Container
                    style={{
                        flexDirection: 'column',
                        gap: theme.spacing(0.5),
                        padding: theme.spacing(1.5),
                        borderRadius: 6,
                        border: `1px solid ${theme.palette.warning.main}`,
                        backgroundColor: 'rgba(255, 152, 0, 0.08)',
                    }}
                >
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                        Project window looks short
                    </Typography>
                    <Typography variant="body2">
                        Last year’s turn
                        {props.bulkScheduleSource?.name
                            ? ` (${props.bulkScheduleSource.name})`
                            : ''}{' '}
                        was about {sourceDays} days; this project is only{' '}
                        {projectDays} days. Check move-out / move-in and widen
                        the project dates if needed — schedule dates will update
                        when you continue.
                    </Typography>
                </Container>
            )}

            <Container style={{ gap: theme.spacing(2) }}>
                <KeyboardDatePicker
                    label="Project start"
                    value={props.startDate}
                    onChange={(date) => {
                        if (!date) return
                        const next = fromPickerDate(
                            DateTime.fromJSDate(date as Date).setZone(
                                props.timezone,
                            ),
                        )
                        if (next) props.setStartDate(next)
                    }}
                    format="MM/dd/yyyy"
                    inputVariant="outlined"
                    style={{ flex: 1 }}
                />
                <KeyboardDatePicker
                    label="Project end"
                    value={props.endDate}
                    onChange={(date) => {
                        if (!date) return
                        const next = fromPickerDate(
                            DateTime.fromJSDate(date as Date).setZone(
                                props.timezone,
                            ),
                        )
                        if (next) props.setEndDate(next)
                    }}
                    format="MM/dd/yyyy"
                    inputVariant="outlined"
                    style={{ flex: 1 }}
                />
            </Container>

            <FormControl component="fieldset">
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    Would you like to set a budget for this project?
                </Typography>
                <RadioGroup
                    row
                    value={props.wantBudget ?? ''}
                    onChange={(e) =>
                        props.setWantBudget(e.target.value as 'yes' | 'no')
                    }
                >
                    <FormControlLabel
                        value="yes"
                        control={<Radio color="primary" />}
                        label="Yes"
                    />
                    <FormControlLabel
                        value="no"
                        control={<Radio color="primary" />}
                        label="No"
                    />
                </RadioGroup>
            </FormControl>
            {props.wantBudget === 'yes' && (
                <NumberFormat
                    customInput={TextField}
                    label="Budget amount"
                    variant="outlined"
                    fullWidth
                    thousandSeparator
                    prefix="$"
                    value={props.budgetAmount}
                    onValueChange={(values) =>
                        props.setBudgetAmount(values.value)
                    }
                />
            )}
        </Container>
    )
}

function UnitsStep(props: {
    infrastructure: import('../../../../models').Folder
    phaseTemplateUnits: TemplateUnit[]
    phaseId: number
    setTemplateUnits: (params: {
        ordered_units: { unitId: number; order: number }[]
    }) => void | Promise<unknown>
    phaseIndex: number
    phaseCount: number
    flushRef: React.MutableRefObject<(() => void | Promise<void>) | null>
    selectedCountRef: React.MutableRefObject<number>
}) {
    const theme = useTheme()
    const { finderSelection, setSelection } = useFinderSelection('schedule')
    const initializedRef = useRef(false)

    useEffect(() => {
        if (initializedRef.current) return
        const selected = props.phaseTemplateUnits
            .filter((tu) => tu.is_included)
            .map((tu) => tu.unit)
        setSelection(selected)
        initializedRef.current = true
    }, [props.phaseTemplateUnits, setSelection])

    // Before seed, show template includes; after seed, trust finder only —
    // template is_included lags until flush (clearing must not pass validation).
    const includedCount = initializedRef.current
        ? finderSelection.unit.length
        : props.phaseTemplateUnits.filter((tu) => tu.is_included).length
    props.selectedCountRef.current = includedCount

    return (
        <Container style={{ flexDirection: 'column', height: '100%' }}>
            <Typography
                variant="body1"
                style={{ marginBottom: theme.spacing(1) }}
            >
                {(props.phaseCount > 1 ? `Phase ${props.phaseIndex}: ` : '') +
                    `${includedCount} unit${
                        includedCount === 1 ? '' : 's'
                    } selected for this turn. Confirm the selection or adjust it in the tree.`}
            </Typography>
            <Container
                style={{
                    flex: 1,
                    minHeight: 420,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: 4,
                    overflow: 'hidden',
                }}
            >
                <UnitSelector
                    infrastructure={props.infrastructure}
                    finderSelection={finderSelection}
                    onUnitSelectionChange={(_units: Unit[]) => {
                        // UnitSelector persists via setTemplateUnits debounce
                    }}
                    setTemplateUnits={props.setTemplateUnits}
                    flushRef={props.flushRef}
                    phaseId={props.phaseId}
                    initialSelectedUnitCount={
                        props.phaseTemplateUnits.filter((tu) => tu.is_included)
                            .length
                    }
                    defaultOpenFirstLevel
                    height="100%"
                />
            </Container>
        </Container>
    )
}

function DatesStep(props: {
    workStartDate?: string | null
    workEndDate?: string | null
    transferStartDate?: string | null
    transferEndDate?: string | null
    scheduleStart?: string
    scheduleEnd?: string
    timezone: string
    phaseIndex: number
    phaseCount: number
    updateEzSchedule: (params: {
        work_start_date?: DateTime | null
        work_end_date?: DateTime | null
        transfer_start_date?: string | null
        transfer_end_date?: string | null
    }) => void
}) {
    const theme = useTheme()
    const parse = (iso?: string | null) =>
        iso
            ? DateTime.fromISO(iso).setZone(props.timezone).startOf('day')
            : null

    const workStart = parse(props.workStartDate)
    const workEnd = parse(props.workEndDate)
    const transferStart = parse(props.transferStartDate)
    const transferEnd = parse(props.transferEndDate)

    return (
        <Container style={{ flexDirection: 'column', gap: theme.spacing(2) }}>
            <Typography variant="body1">
                {(props.phaseCount > 1 ? `Phase ${props.phaseIndex}: ` : '') +
                    'These are the work and transfer windows for this turn. They were copied from a prior year — adjust if your timing changed.'}
            </Typography>
            <Container style={{ gap: theme.spacing(2) }}>
                <KeyboardDatePicker
                    label="Work start"
                    value={workStart}
                    onChange={(date) => {
                        if (date === null) {
                            props.updateEzSchedule({ work_start_date: null })
                            return
                        }
                        const next = fromPickerDate(
                            DateTime.fromJSDate(date as Date).setZone(
                                props.timezone,
                            ),
                        )
                        props.updateEzSchedule({ work_start_date: next })
                    }}
                    format="MM/dd/yyyy"
                    inputVariant="outlined"
                    style={{ flex: 1 }}
                />
                <KeyboardDatePicker
                    label="Work end"
                    value={workEnd}
                    onChange={(date) => {
                        if (date === null) {
                            props.updateEzSchedule({ work_end_date: null })
                            return
                        }
                        const next = fromPickerDate(
                            DateTime.fromJSDate(date as Date).setZone(
                                props.timezone,
                            ),
                        )
                        props.updateEzSchedule({ work_end_date: next })
                    }}
                    format="MM/dd/yyyy"
                    inputVariant="outlined"
                    style={{ flex: 1 }}
                />
            </Container>
            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                Transfer dates (optional)
            </Typography>
            <Container style={{ gap: theme.spacing(2) }}>
                <KeyboardDatePicker
                    label="Transfer start"
                    value={transferStart}
                    clearable
                    onChange={(date) => {
                        if (date === null) {
                            props.updateEzSchedule({
                                transfer_start_date: null,
                            })
                            return
                        }
                        const next = fromPickerDate(
                            DateTime.fromJSDate(date as Date).setZone(
                                props.timezone,
                            ),
                        )
                        props.updateEzSchedule({
                            transfer_start_date: next ? next.toISO() : null,
                        })
                    }}
                    format="MM/dd/yyyy"
                    inputVariant="outlined"
                    style={{ flex: 1 }}
                />
                <KeyboardDatePicker
                    label="Transfer end"
                    value={transferEnd}
                    clearable
                    onChange={(date) => {
                        if (date === null) {
                            props.updateEzSchedule({
                                transfer_end_date: null,
                            })
                            return
                        }
                        const next = fromPickerDate(
                            DateTime.fromJSDate(date as Date).setZone(
                                props.timezone,
                            ),
                        )
                        props.updateEzSchedule({
                            transfer_end_date: next ? next.toISO() : null,
                        })
                    }}
                    format="MM/dd/yyyy"
                    inputVariant="outlined"
                    style={{ flex: 1 }}
                />
            </Container>
        </Container>
    )
}

function ServicesStep(props: {
    templateServices: import('../../../../models').TemplateService[]
    vendorList: ListVendor[]
    serviceList: Service[]
    scheduleId: number
    workStartDate?: string | null
    workEndDate?: string | null
    phaseIndex: number
    phaseCount: number
    timezone: string
    addTemplateService: (params: {
        serviceId: number
        serviceName: string
        scheduleId: number
    }) => void
    deleteTemplateService: (params: { templateServiceId: number }) => void
    updateTemplateService: (params: {
        templateServiceId: number
        budget?: number
        stand_alone?: boolean
        position?: number
    }) => void
    addTemplateServiceVendor: (params: {
        templateServiceId: number
        vendorId: number
        vendorName: string
    }) => void
    deleteTemplateServiceVendor: (params: {
        templateServiceVendorId: number
    }) => void
    updateTemplateServiceVendor: (params: {
        templateServiceVendorId: number
        start_date?: string
        end_date?: string
        auto_invoice?: boolean
        capacity?: number
    }) => void
}) {
    const theme = useTheme()
    const popoverState = usePopover()
    const canAdd = Boolean(props.workStartDate && props.workEndDate)

    const availableServices = useMemo(() => {
        const used = new Set(props.templateServices.map((ts) => ts.service.id))
        return props.serviceList.filter(
            (s) =>
                !used.has(s.id) &&
                props.vendorList.some((v) =>
                    v.services?.some((vs) => vs.id === s.id),
                ),
        )
    }, [props.serviceList, props.templateServices, props.vendorList])

    return (
        <Container style={{ flexDirection: 'column', gap: theme.spacing(1) }}>
            <Container
                style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography
                    variant="body1"
                    style={{ flex: 1, marginRight: 16 }}
                >
                    {(props.phaseCount > 1
                        ? `Phase ${props.phaseIndex}: `
                        : '') +
                        'Confirm each service and its vendor. Service dates follow the work window you set — swap vendors or add a missing service if needed.'}
                </Typography>
                <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    color="primary"
                    disabled={!canAdd}
                    onClick={popoverState.handleOpen}
                >
                    Add service
                </Button>
            </Container>
            {props.templateServices.map((ts, index) => (
                <Container
                    key={ts.id}
                    style={{ flexDirection: 'column', marginBottom: 8 }}
                >
                    <Typography variant="caption" color="textSecondary">
                        Service window:{' '}
                        {ts.start_date
                            ? DateTime.fromISO(ts.start_date)
                                  .setZone(props.timezone)
                                  .toFormat('MMM d, yyyy')
                            : '—'}{' '}
                        –{' '}
                        {ts.end_date
                            ? DateTime.fromISO(ts.end_date)
                                  .setZone(props.timezone)
                                  .toFormat('MMM d, yyyy')
                            : '—'}
                    </Typography>
                    <ServiceRow
                        theme={theme}
                        templateService={ts}
                        vendorList={props.vendorList}
                        index={index}
                        serviceCount={props.templateServices.length}
                        deleteTemplateService={props.deleteTemplateService}
                        updateTemplateService={props.updateTemplateService}
                        addTemplateServiceVendor={
                            props.addTemplateServiceVendor
                        }
                        deleteTemplateServiceVendor={
                            props.deleteTemplateServiceVendor
                        }
                        updateTemplateServiceVendor={
                            props.updateTemplateServiceVendor
                        }
                    />
                </Container>
            ))}
            <SelectorPopover
                theme={theme}
                popoverState={popoverState}
                data={availableServices}
                label="Select Service"
                onSubmit={(selectedId) => {
                    const service = availableServices.find(
                        (s) => s.id === selectedId,
                    )
                    if (!service) return
                    props.addTemplateService({
                        serviceId: service.id,
                        serviceName: service.name,
                        scheduleId: props.scheduleId,
                    })
                }}
            />
        </Container>
    )
}
